import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { HkdInfo } from "../api";

interface PLEntry {
  purchase_date: string;
  seller_name: string;
  seller_address?: string | null;
  seller_id_card?: string | null;
  item_description: string;
  quantity: number;
  unit?: string | null;
  unit_price?: number | null;
  total_amount: number;
  note?: string | null;
}

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

function formatDateVi(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function setCell(
  ws: ExcelJS.Worksheet,
  row: number,
  col: number,
  value: ExcelJS.CellValue,
  opts?: { bold?: boolean; align?: Partial<ExcelJS.Alignment>; border?: boolean; numFmt?: string; italic?: boolean }
) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  const font: Partial<ExcelJS.Font> = {};
  if (opts?.bold) font.bold = true;
  if (opts?.italic) font.italic = true;
  if (Object.keys(font).length) cell.font = font;
  if (opts?.align) cell.alignment = opts.align;
  if (opts?.border) cell.border = THIN_BORDER;
  if (opts?.numFmt) cell.numFmt = opts.numFmt;
  return cell;
}

/**
 * Xuất Bảng kê 01/TNDN (TT78/2014) — Thu mua hàng hóa, dịch vụ không có hóa đơn.
 */
export async function exportBangKe01TNDN(params: {
  hkd: HkdInfo;
  entries: PLEntry[];
  from?: string;
  to?: string;
}): Promise<void> {
  const { hkd, entries, from, to } = params;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Bảng kê 01-TNDN");

  // Column widths (10 cột)
  ws.getColumn(1).width = 6;   // STT
  ws.getColumn(2).width = 14;  // Ngày tháng năm mua
  ws.getColumn(3).width = 22;  // Tên người bán
  ws.getColumn(4).width = 24;  // Địa chỉ
  ws.getColumn(5).width = 18;  // Số CMND/CCCD/MST
  ws.getColumn(6).width = 28;  // Tên mặt hàng
  ws.getColumn(7).width = 9;   // Số lượng
  ws.getColumn(8).width = 14;  // Đơn giá
  ws.getColumn(9).width = 16;  // Tổng giá trị
  ws.getColumn(10).width = 14; // Ghi chú

  let row = 1;

  // Mẫu số — góc phải
  ws.mergeCells(row, 7, row, 10);
  setCell(ws, row, 7, "Mẫu số 01/TNDN", { bold: true, align: { horizontal: "center" } });
  row++;
  ws.mergeCells(row, 7, row, 10);
  setCell(ws, row, 7, "(Ban hành kèm theo Thông tư số 78/2014/TT-BTC ngày 18/6/2014 của Bộ Tài chính)", {
    italic: true,
    align: { horizontal: "center", wrapText: true },
  });
  row += 2;

  // Khối thông tin HKD — trái
  ws.mergeCells(row, 1, row, 6);
  setCell(ws, row, 1, `Tên hộ kinh doanh/doanh nghiệp: ${hkd.name || ""}`, { bold: true });
  row++;
  ws.mergeCells(row, 1, row, 6);
  setCell(ws, row, 1, `Mã số thuế: ${hkd.tax_code || ""}`);
  row++;
  ws.mergeCells(row, 1, row, 6);
  setCell(ws, row, 1, `Địa chỉ: ${hkd.address || ""}`);
  row += 2;

  // Tiêu đề giữa
  ws.mergeCells(row, 1, row, 10);
  setCell(ws, row, 1, "BẢNG KÊ THU MUA HÀNG HÓA, DỊCH VỤ MUA VÀO KHÔNG CÓ HÓA ĐƠN", {
    bold: true,
    align: { horizontal: "center", vertical: "middle" },
  });
  row++;

  // Kỳ
  if (from || to) {
    ws.mergeCells(row, 1, row, 10);
    const fromStr = from ? formatDateVi(from) : "...";
    const toStr = to ? formatDateVi(to) : "...";
    setCell(ws, row, 1, `Từ ngày ${fromStr} đến ngày ${toStr}`, {
      italic: true,
      align: { horizontal: "center" },
    });
  }
  row += 2;

  // Header bảng
  const headers = [
    "STT",
    "Ngày tháng\nnăm mua",
    "Tên người bán",
    "Địa chỉ\nngười bán",
    "Số CMND/CCCD\n/MST người bán",
    "Tên mặt hàng/\ndịch vụ",
    "Số lượng",
    "Đơn giá",
    "Tổng giá trị\nhàng hóa",
    "Ghi chú",
  ];
  for (let c = 0; c < headers.length; c++) {
    setCell(ws, row, c + 1, headers[c], {
      bold: true,
      align: { horizontal: "center", vertical: "middle", wrapText: true },
      border: true,
    });
  }
  row++;

  // Dòng số thứ tự cột
  const colNums = ["(1)", "(2)", "(3)", "(4)", "(5)", "(6)", "(7)", "(8)", "(9)", "(10)"];
  for (let c = 0; c < colNums.length; c++) {
    setCell(ws, row, c + 1, colNums[c], {
      align: { horizontal: "center" },
      border: true,
      italic: true,
    });
  }
  row++;

  // Data rows
  let totalSum = 0;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    setCell(ws, row, 1, i + 1, { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 2, formatDateVi(e.purchase_date), { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 3, e.seller_name || "", { border: true });
    setCell(ws, row, 4, e.seller_address || "", { border: true });
    setCell(ws, row, 5, e.seller_id_card || "", { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 6, e.item_description || "", { border: true });
    setCell(ws, row, 7, e.quantity || 0, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
    setCell(ws, row, 8, e.unit_price || 0, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
    setCell(ws, row, 9, e.total_amount || 0, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
    setCell(ws, row, 10, "", { border: true });
    totalSum += e.total_amount || 0;
    row++;
  }

  // Dòng Tổng cộng
  ws.mergeCells(row, 1, row, 8);
  setCell(ws, row, 1, "Tổng cộng", { bold: true, border: true, align: { horizontal: "center" } });
  setCell(ws, row, 9, totalSum, { bold: true, border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
  setCell(ws, row, 10, "", { border: true });
  row += 3;

  // Khối ký — phải
  const now = new Date();
  ws.mergeCells(row, 6, row, 10);
  setCell(ws, row, 6, `Ngày ${now.getDate()} tháng ${now.getMonth() + 1} năm ${now.getFullYear()}`, {
    italic: true,
    align: { horizontal: "center" },
  });
  row += 2;

  setCell(ws, row, 3, "NGƯỜI LẬP BẢNG KÊ", { bold: true, align: { horizontal: "center" } });
  setCell(ws, row, 8, "NGƯỜI ĐẠI DIỆN\nTHEO PHÁP LUẬT", {
    bold: true,
    align: { horizontal: "center", wrapText: true },
  });
  row++;
  setCell(ws, row, 3, "(Ký, ghi rõ họ tên)", { italic: true, align: { horizontal: "center" } });
  setCell(ws, row, 8, "(Ký, ghi rõ họ tên)", { italic: true, align: { horizontal: "center" } });
  row += 4;
  setCell(ws, row, 3, hkd.preparer?.trim() || "", { align: { horizontal: "center" } });
  setCell(ws, row, 8, hkd.owner?.trim() || "", { align: { horizontal: "center" } });

  // Save file
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const datePart = from && to ? `${from}_${to}` : new Date().toISOString().slice(0, 10);
  const filename = `BangKe-01TNDN_${datePart}.xlsx`;
  saveAs(blob, filename);
}
